/**
 * Simulation engine for mission games - the browser twin of backend services/game_builder/sim.py
 * (same semantics; the solver there proves a mission is winnable using these exact rules).
 * Pure functions over an immutable context: { state, done, seen, fired }.
 *   condition: compare {item, prop, op, value} | {all} | {any} | {not} | {seen} | {done}
 *   effect:    {op: set|add|append, item, prop, value}
 *   event:     fires once, the first time `when` is true after an action
 * Compare ops and effect ops are plugins (./mechanics/engine.js, twin of the backend's mechanics/core.py).
 */
import { COMPARES, EFFECTS } from './mechanics/engine';
import { INTERACTIONS } from './mechanics/interactions';

const MAX_EVENT_PASSES = 20;
const clone = (v) => JSON.parse(JSON.stringify(v));

export function initialContext(spec) {
  return {
    state: Object.fromEntries(spec.items.map((it) => [it.id, clone(it.state || {})])),
    done: [],
    seen: [],
    fired: []
  };
}

export function evaluate(cond, ctx) {
  if (cond === null || cond === undefined) return true;
  if (cond.all) return cond.all.every((c) => evaluate(c, ctx));
  if (cond.any) return cond.any.some((c) => evaluate(c, ctx));
  if (cond.not) return !evaluate(cond.not, ctx);
  if (cond.seen !== undefined) return ctx.seen.includes(cond.seen);
  if (cond.done !== undefined) return ctx.done.includes(cond.done);
  const compare = COMPARES.get(cond.op);
  return compare ? compare.test(ctx.state[cond.item]?.[cond.prop], cond.value) : false;
}

function applyEffects(effects, state) {
  const next = clone(state);
  (effects || []).forEach((e) => {
    const props = next[e.item] || (next[e.item] = {});
    const effect = EFFECTS.get(e.op);
    if (effect) effect.apply(props, e.prop, e.value);
  });
  return next;
}

function runEvents(spec, ctx) {
  let current = ctx;
  const feedback = [];
  for (let pass = 0; pass < MAX_EVENT_PASSES; pass += 1) {
    const before = current;   // events to fire are picked from the state at the start of the pass (as in sim.py)
    const newly = (spec.events || []).filter((ev) => !before.fired.includes(ev.id) && evaluate(ev.when, before));
    if (!newly.length) break;
    let { state, fired } = before;
    for (const ev of newly) {
      state = applyEffects(ev.effects, state);
      fired = [...fired, ev.id];
      feedback.push(ev.feedback);
    }
    current = { ...before, state, fired };
  }
  return { ctx: current, feedback };
}

/** Actions on the same item by the same interaction share ONE control, told further apart by the
 * interaction's slot field (a write by what it's about, a move by where it goes, an adjust by the
 * amount) - as in sim.py slot_key. */
export const slotKey = (a) => JSON.stringify([a.item, a.interaction, a[INTERACTIONS.get(a.interaction)?.slotField || 'drop_on'] ?? null]);

/** What using that control does now: the first pending action in its slot whose `requires` holds,
 * else the first pending one (its `blocked` line explains why) - as in sim.py resolve. */
export function resolveSlot(spec, action, ctx) {
  const key = slotKey(action);
  const queue = spec.actions.filter((a) => slotKey(a) === key && !ctx.done.includes(a.id));
  return queue.find((a) => evaluate(a.requires, ctx)) || queue[0] || action;
}

/** Every distinct move the player can make from here (one per control). */
function moves(spec, c, onlyRight = false) {
  const seen = new Set();
  return spec.actions
    .filter((a) => !c.done.includes(a.id) && (!onlyRight || a.kind === 'right'))
    .map((a) => resolveSlot(spec, a, c))
    .filter((a) => (seen.has(a.id) ? false : seen.add(a.id)) && (!onlyRight || a.kind === 'right'));
}

/** Returns { result: 'blocked' | 'done', ctx, feedback: [feedback objects] } - ctx is a new object. */
export function perform(spec, action, ctx) {
  if (!evaluate(action.requires, ctx)) return { result: 'blocked', ctx, feedback: [action.blocked].filter(Boolean) };
  // A repeatable action (pour to 10 ml) can be done again, so it's never marked done.
  const after = { ...ctx, state: applyEffects(action.effects, ctx.state), done: action.repeatable ? ctx.done : [...ctx.done, action.id] };
  const { ctx: settled, feedback } = runEvents(spec, after);
  return { result: 'done', ctx: settled, feedback: [action.feedback, ...feedback].filter(Boolean) };
}

const MAX_SEARCH_STATES = 5000;

/** Can any order of the remaining actions still reach the goal without hitting `fail`? (Like the
 * backend solver, the player can always look at things, so every item counts as seen.) */
export function canStillWin(spec, ctx) {
  // An inventory flow can't be lost before its last step: every pick can be redone, every move undone, and the
  // hand-over is where it's judged (backend flows.py). Searching it is wasted work - and with several things to
  // sort or switch, every combination of them is too many to search while the player waits.
  if (spec.type === 'flow') return true;
  const start = { ...ctx, seen: spec.items.map((it) => it.id) };
  const queue = [start];
  const visited = new Set();
  while (queue.length && visited.size < MAX_SEARCH_STATES) {
    const c = queue.shift();
    const key = JSON.stringify([c.state, [...c.done].sort(), [...c.fired].sort()]);
    if (visited.has(key)) continue;
    visited.add(key);
    if (spec.fail && evaluate(spec.fail, c)) continue;
    if (evaluate(spec.goal, c)) return true;
    moves(spec, c).forEach((a) => {
      const out = perform(spec, a, c);
      if (out.result === 'done') queue.push(out.ctx);
    });
  }
  return visited.size >= MAX_SEARCH_STATES;   // too big to tell - don't end the game on a guess
}

/** The shortest list of action ids from here to the goal (never through `fail`), or null. For hints. */
export function nextSteps(spec, ctx) {
  const start = { ctx: { ...ctx, seen: spec.items.map((it) => it.id) }, path: [] };
  if (spec.type === 'flow') return walkForward(spec, start.ctx);
  const queue = [start];
  const visited = new Set();
  while (queue.length && visited.size < MAX_SEARCH_STATES) {
    const { ctx: c, path } = queue.shift();
    const key = JSON.stringify([c.state, [...c.done].sort(), [...c.fired].sort()]);
    if (visited.has(key)) continue;
    visited.add(key);
    if (spec.fail && evaluate(spec.fail, c)) continue;
    if (evaluate(spec.goal, c)) return path;
    moves(spec, c, true).forEach((a) => {
      const out = perform(spec, a, c);
      if (out.result === 'done') queue.push({ ctx: out.ctx, path: [...path, a.id] });
    });
  }
  return null;
}

/** A flow's hints: walk forward one right move at a time - the first that's allowed now and changes something
 * (a flow has no traps in the way, so the first such move is always progress). */
function walkForward(spec, from) {
  let c = from;
  const path = [];
  const progress = (here) => {          // the first right move from here that's allowed and changes something
    const was = JSON.stringify(here.state);
    return moves(spec, here, true).map((a) => [a, perform(spec, a, here)])
      .find(([a, out]) => out.result === 'done' && (!a.repeatable || JSON.stringify(out.ctx.state) !== was));
  };
  for (let n = 0; n < 80 && !evaluate(spec.goal, c); n += 1) {
    const step = progress(c);
    if (!step) return null;
    c = step[1].ctx;
    path.push(step[0].id);
  }
  return evaluate(spec.goal, c) ? path : null;
}

/** 'goal' | 'fail' | 'dead_end' (a wrong order left the goal unreachable - the mission is lost) | 'playing' */
export function status(spec, ctx) {
  if (spec.fail && evaluate(spec.fail, ctx)) return 'fail';
  if (evaluate(spec.goal, ctx)) return 'goal';
  if (!canStillWin(spec, ctx)) return 'dead_end';
  return 'playing';
}
